import { useContext, useState } from 'react';

import Button from '../../reusable/button/button.component';

import { ToastContext } from '../../../contexts/toast.context';

import Client from '../../../tools/client';

import {
  ButtonRow,
  Entry,
  Error,
  Form,
  Row,
  Results,
  Title,
  Wrapper
} from './manual-giveaway-display.styles';

const client = new Client();

const getTodayDateString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0]; // 'yyyy-mm-dd'
};

const ManualGiveawayDisplay = () => {
  const [startDate, setStartDate] = useState(getTodayDateString());
  const [endDate, setEndDate] = useState(getTodayDateString());
  const [amount, setAmount] = useState(1);
  const [type, setType] = useState('user');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const { errorToast, successToast } = useContext(ToastContext);

  const toUTCISOString = (dateString, endOfDay = false) => {
    const date = new Date(dateString);
    if (endOfDay) {
      date.setUTCHours(23, 59, 59, 999);
    } else {
      date.setUTCHours(0, 0, 0, 0);
    }
    return date.toISOString();
  };

  const handleClearDates = () => {
    setStartDate('');
    setEndDate('');
    successToast('Date filters cleared.');
  };

  const handlePick = async () => {
    const now = new Date();

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        errorToast('Start date cannot be after end date.');
        return;
      }

      if (start > now || end > now) {
        errorToast('Dates cannot be in the future.');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        start: startDate ? toUTCISOString(startDate) : null,
        end: endDate ? toUTCISOString(endDate, true) : null,
        amount
      };

      const data =
        type === 'user'
          ? await client.getRandomUsersForGiveaway(payload)
          : await client.getRandomOrdersForGiveaway(payload);

      setResults(data);
      successToast('Giveaway draw completed.');
    } catch (err) {
      console.error('Failed to pick:', err);
      setResults({ error: 'Failed to fetch results' });
      errorToast('Failed to fetch results.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Title>Manual Giveaway Picker</Title>
      <Form>
        <Row>
          <label>Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
        </Row>
        <Row>
          <label>End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
        </Row>
        <Row>
          <label>Amount:</label>
          <input
            type="number"
            value={amount}
            min={1}
            onChange={e => setAmount(e.target.value)}
          />
        </Row>
        <Row>
          <label>Type:</label>
          <select value={type} onChange={e => setType(e.target.value)}>
            <option value="user">User</option>
            <option value="order">Order</option>
          </select>
        </Row>
        <ButtonRow>
          <Button onClick={handleClearDates} disabled={loading}>
            Clear Dates
          </Button>
          <Button onClick={handlePick} disabled={loading}>
            {loading ? 'Picking...' : 'Pick Random'}
          </Button>
        </ButtonRow>
      </Form>
      <Results>
        {results?.error && <Error>{results.error}</Error>}

        {results?.range && (
          <Entry>
            <strong>Range:</strong>{' '}
            {new Date(results.range.start).toLocaleString()} -{' '}
            {new Date(results.range.end).toLocaleString()}
          </Entry>
        )}

        {results?.drawId && (
          <Entry>
            <strong>Draw ID:</strong> {results.drawId}
          </Entry>
        )}

        {results?.elapsedMs != null && (
          <Entry>
            <strong>Elapsed Time:</strong> {results.elapsedMs}ms
          </Entry>
        )}

        {results?.totalUsers != null && (
          <Entry>
            <strong>Total Users:</strong> {results.totalUsers}
          </Entry>
        )}

        {results?.totalOrders != null && (
          <Entry>
            <strong>Total Orders:</strong> {results.totalOrders}
          </Entry>
        )}

        {results?.winners?.length > 0 && (
          <>
            <Entry><strong>Winners:</strong></Entry>
            {results.winners.map(winner => (
              <Entry key={winner.id}>
                <a href={`/accounts/${winner.id}`} target="_blank" rel="noopener noreferrer">
                  #{winner.rank} - {winner.username}
                </a>
              </Entry>
            ))}
          </>
        )}

        {results?.entryPool?.length > 0 && (
          <>
            <Entry><strong>Entry Pool ({results.entryPool.length}):</strong></Entry>
            {results.entryPool.map(entry => (
              <Entry key={entry.id}>{entry.username}</Entry>
            ))}
          </>
        )}
      </Results>
    </Wrapper>
  );
};

export default ManualGiveawayDisplay;